// File: src/hooks/useUserPermissions.js
import { useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthProvider";

/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────── */

function normalizeToArray(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeRoleValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeStringArray(value) {
  return Array.from(
    new Set(
      normalizeToArray(value)
        .map(normalizeRoleValue)
        .filter(Boolean)
    )
  );
}

function normalizePositiveIds(value) {
  return Array.from(
    new Set(
      normalizeToArray(value)
        .map(Number)
        .filter(
          (id) =>
            Number.isInteger(id) &&
            id > 0
        )
    )
  );
}

/* ──────────────────────────────────────────────────────────────
 * Transitional role buckets
 *
 * IMPORTANT:
 *
 * These are UI convenience mappings only.
 *
 * They are NOT backend authorization.
 *
 * Long-term these should gradually be replaced by explicit backend
 * permissions such as:
 *
 *   procurement.view
 *   po.create
 *   po.approve
 *   invoice.approve
 *   invoice.pay
 *   inventory.adjust
 *   maintenance.manage
 *
 * Until then, these preserve current UX behavior without trusting
 * localStorage or decoded JWT claims.
 * ────────────────────────────────────────────────────────────── */

export const PERMISSION_BUCKETS = Object.freeze({
  procurement: [
    "admin",
    "developer",
    "procurement",
    "manager",
    "finance",
  ],

  inventory: [
    "admin",
    "developer",
    "procurement",
    "finance",
    "inventory",
    "manager",
  ],

  staffing: [
    "admin",
    "developer",
    "manager",
    "staffing",
    "finance",
  ],

  maintenance: [
    "admin",
    "developer",
    "manager",
  ],

  eventPlanning: [
    "admin",
    "developer",
    "manager",
    "planner",
    "finance",
  ],

  timesheets: [
    "admin",
    "developer",
    "finance",
    "manager",
  ],

  finance: [
    "admin",
    "finance",
    "developer",
    "manager",
  ],

  revenue: [
    "admin",
    "finance",
    "developer",
    "manager",
  ],

  executive: [
    "admin",
    "finance",
    "developer",
    "manager",
  ],

  eventMenu: [
    "admin",
    "developer",
    "manager",
    "planner",
    "inventory",
    "procurement",
    "finance",
  ],

  eventStaffing: [
    "admin",
    "developer",
    "manager",
    "staffing",
    "finance",
  ],

  eventProcurement: [
    "admin",
    "developer",
    "manager",
    "procurement",
    "inventory",
    "finance",
  ],

  eventBilling: [
    "admin",
    "developer",
    "manager",
    "planner",
    "finance",
  ],

  eventReports: [
    "admin",
    "developer",
    "manager",
    "finance",
  ],

  managerReports: [
    "admin",
    "developer",
    "manager",
    "finance",
  ],
});

/* ──────────────────────────────────────────────────────────────
 * Route-to-bucket convenience map
 *
 * Unknown paths are intentionally denied by canAccessPath().
 * App.jsx/ProtectedRoute remain responsible for actual route gating.
 * ────────────────────────────────────────────────────────────── */

export const ROUTE_BUCKET_MAP = Object.freeze({
  "/procurement": "procurement",
  "/inventory": "inventory",
  "/staffing-dashboard": "staffing",
  "/admin-maintenance": "maintenance",
  "/events": "eventPlanning",
  "/events/reports": "eventReports",
});

/* ──────────────────────────────────────────────────────────────
 * Hook
 * ────────────────────────────────────────────────────────────── */

export function useUserPermissions() {
  const auth = useAuth();

  /*
   * Single source of truth:
   *
   * AuthProvider.user
   *
   * That user came from server-validated /auth/me.
   *
   * No UserContext fallback.
   * No localStorage.
   * No JWT decoding.
   */
  const userRaw =
    auth?.user || null;

  const authenticated =
    Boolean(
      auth?.authenticated &&
      userRaw?.id
    );

  const user =
    useMemo(() => {
      if (!authenticated) {
        return null;
      }

      const role =
        normalizeRoleValue(
          userRaw?.role
        );

      const roles =
        Array.from(
          new Set([
            ...(role
              ? [role]
              : []),

            ...normalizeStringArray(
              userRaw?.roles
            ),
          ])
        );

      const permissions =
        normalizeStringArray(
          userRaw?.permissions
        );

      const modules =
        normalizeToArray(
          userRaw?.modules
        )
          .map((value) =>
            String(value || "")
              .trim()
          )
          .filter(Boolean);

      const allowedCostCentres =
        normalizePositiveIds(
          userRaw?.allowedCostCentres ??
          userRaw?.cost_centres
        );

      return {
        ...userRaw,

        role:
          role || null,

        roles,

        permissions,

        modules,

        allowedCostCentres,

        /*
         * Compatibility alias for older UI consumers.
         */
        cost_centres:
          allowedCostCentres,

        is_platform_admin:
          userRaw?.is_platform_admin ===
          true,
      };
    }, [
      authenticated,
      userRaw,
    ]);

  const roles =
    useMemo(
      () =>
        user?.roles || [],
      [user]
    );

  const permissions =
    useMemo(
      () =>
        user?.permissions || [],
      [user]
    );

  const role =
    user?.role || null;

  const rolesSet =
    useMemo(
      () =>
        new Set(roles),
      [roles]
    );

  const permissionsSet =
    useMemo(
      () =>
        new Set(
          permissions
        ),
      [permissions]
    );

  /* ────────────────────────────────────────────────────────────
   * Role flags
   * ──────────────────────────────────────────────────────────── */

  const flags =
    useMemo(
      () => ({
        isAdmin:
          rolesSet.has(
            "admin"
          ),

        isFinance:
          rolesSet.has(
            "finance"
          ),

        isDeveloper:
          rolesSet.has(
            "developer"
          ),

        isProcurement:
          rolesSet.has(
            "procurement"
          ),

        isManager:
          rolesSet.has(
            "manager"
          ),

        isViewer:
          rolesSet.has(
            "viewer"
          ),

        isInventory:
          rolesSet.has(
            "inventory"
          ),

        isPlanner:
          rolesSet.has(
            "planner"
          ),

        isStaffing:
          rolesSet.has(
            "staffing"
          ),

        isPlatformAdmin:
          user?.is_platform_admin ===
          true,
      }),
      [
        rolesSet,
        user,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Role helpers
   *
   * No universal admin/developer bypass.
   *
   * If a role must be allowed, it should appear explicitly in the
   * route/bucket definition until granular permissions replace roles.
   * ──────────────────────────────────────────────────────────── */

  const hasRole =
    useCallback(
      (wantedRole) => {
        if (!authenticated) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedRole
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            rolesSet.has(
              candidate
            )
        );
      },
      [
        authenticated,
        rolesSet,
      ]
    );

  const hasAnyRole =
    useCallback(
      (wantedRoles = []) => {
        if (!authenticated) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedRoles
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            rolesSet.has(
              candidate
            )
        );
      },
      [
        authenticated,
        rolesSet,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Explicit permission helpers
   *
   * These become increasingly important as backend permissions are
   * introduced.
   * ──────────────────────────────────────────────────────────── */

  const hasPermission =
    useCallback(
      (wantedPermission) => {
        if (!authenticated) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedPermission
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            permissionsSet.has(
              candidate
            )
        );
      },
      [
        authenticated,
        permissionsSet,
      ]
    );

  const hasAnyPermission =
    useCallback(
      (
        wantedPermissions = []
      ) => {
        if (!authenticated) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedPermissions
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            permissionsSet.has(
              candidate
            )
        );
      },
      [
        authenticated,
        permissionsSet,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Transitional bucket access
   *
   * FAIL CLOSED:
   *
   * unknown bucket → false
   * no authenticated user → false
   * empty role mapping → false
   * ──────────────────────────────────────────────────────────── */

  const can =
    useCallback(
      (bucket) => {
        if (!authenticated) {
          return false;
        }

        const key =
          String(
            bucket || ""
          ).trim();

        if (!key) {
          return false;
        }

        if (roles.includes('company_director')) {
          const viewPermission = {
            procurement: 'procurement.po.view',
            inventory: 'inventory.stock.view',
            staffing: 'staffing.staffing.view',
            maintenance: 'maintenance.task.view',
            eventPlanning: 'event.event.view',
            eventReports: 'report.operational.view',
          }[key];
          return Boolean(viewPermission && permissionsSet.has(viewPermission));
        }

        const allowed =
          PERMISSION_BUCKETS[
            key
          ];

        if (
          !Array.isArray(
            allowed
          ) ||
          allowed.length === 0
        ) {
          return false;
        }

        const allowedSet =
          new Set(
            allowed
              .map(
                normalizeRoleValue
              )
              .filter(Boolean)
          );

        return roles.some(
          (currentRole) =>
            allowedSet.has(
              currentRole
            )
        );
      },
      [
        authenticated,
        roles,
        permissionsSet,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Route helper
   *
   * Unknown path → false.
   *
   * This helper is not a universal "is authenticated" fallback.
   * ──────────────────────────────────────────────────────────── */

  const canAccessPath =
    useCallback(
      (path) => {
        if (!authenticated) {
          return false;
        }

        const normalizedPath =
          String(
            path || ""
          )
            .trim()
            .split("?")[0]
            .replace(
              /\/+$/,
              ""
            ) || "/";

        const bucket =
          ROUTE_BUCKET_MAP[
            normalizedPath
          ];

        if (!bucket) {
          return false;
        }

        return can(
          bucket
        );
      },
      [
        authenticated,
        can,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Cost-centre access
   *
   * FAIL CLOSED unless backend explicitly grants all scope.
   * ──────────────────────────────────────────────────────────── */

  const inCostCentre =
    useCallback(
      (ccId) => {
        if (
          !authenticated ||
          !user
        ) {
          return false;
        }

        const id =
          Number(ccId);

        if (
          !Number.isInteger(id) ||
          id <= 0
        ) {
          return false;
        }

        /*
         * Future explicit unrestricted scope.
         *
         * Missing/empty cost-centre arrays never mean unrestricted.
         */
        if (
          user.cost_centre_scope ===
            "all" ||
          user.all_cost_centres ===
            true ||
          user.has_all_cost_centres ===
            true
        ) {
          return true;
        }

        const allowed =
          user.allowedCostCentres ||
          [];

        return allowed.includes(
          id
        );
      },
      [
        authenticated,
        user,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Transitional module convenience values
   * ──────────────────────────────────────────────────────────── */

  const canAccessProcurement =
    can("procurement");

  const canAccessInventory =
    can("inventory");

  const canAccessStaffing =
    can("staffing");

  const canAccessMaintenance =
    can("maintenance");

  const canAccessEventPlanning =
    can("eventPlanning");

  const canAccessTimesheets =
    can("timesheets");

  const canSeeFinance =
    can("finance");

  const canSeeRevenue =
    can("revenue");

  const isExecutive =
    can("executive");

  const canViewEventMenu =
    can("eventMenu");

  const canViewEventStaffing =
    can("eventStaffing");

  const canViewEventProcurement =
    can("eventProcurement");

  const canViewEventBilling =
    can("eventBilling");

  const canViewEventReports =
    can("eventReports");

  const canViewManagerReports =
    can("managerReports");

  const allowedBuckets =
    useMemo(
      () =>
        Object.keys(
          PERMISSION_BUCKETS
        ).filter((bucket) =>
          can(bucket)
        ),
      [can]
    );

  return {
    user,

    authenticated,

    role,

    roles,

    permissions,

    ...flags,

    hasRole,
    hasAnyRole,

    hasPermission,
    hasAnyPermission,

    inCostCentre,

    can,
    canAccessPath,

    allowedBuckets,

    // Module access
    canAccessProcurement,
    canAccessProcurementDashboard:
      canAccessProcurement,

    canAccessInventory,
    canAccessStaffing,
    canAccessMaintenance,
    canAccessEventPlanning,
    canAccessTimesheets,

    // Executive / finance
    canSeeFinance,
    canSeeRevenue,
    isExecutive,

    // Event-specific permissions
    canViewEventMenu,
    canViewEventStaffing,
    canViewEventProcurement,
    canViewEventBilling,
    canViewEventReports,
    canViewManagerReports,
  };
}
